import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { MapPinned, ArrowLeft, ArrowRight, Landmark, Sparkles, Store, MapPin, Gavel } from "lucide-react";
import { useApp } from "../context/AppContext";
import { KIND_LABEL } from "../explore";
import { SAR } from "../souq";
import { Countdown } from "../components/SouqLive";
import Seo from "../components/Seo";
import { useBackdrop, pickImage, IMG } from "../lib/backdrop";
import Breadcrumbs from "../components/Breadcrumbs";
import ContentCard from "../components/ContentCard";
import StoryCard from "../components/StoryCard";
import NotFoundPage from "./NotFoundPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
const REL_KIND = { varieties: "varieties", oases: "oases", routes: "routes" };
const LD_TYPE = { variety: "Product", oasis: "Place", route: "TouristTrip" };
const norm = (s) => (s || "").toLowerCase().replace(/\bdates?\b/g, "").replace(/[^a-z0-9]/g, "");

function LiveSouq({ doc, lang, t }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/souq/listings`, { params: { sort: "ending" } })
      .then((r) => setItems((r.data.items || []).filter((l) => norm(l.variety_en) === norm(doc.title_en) || norm(l.variety) === norm(doc.slug))))
      .catch(() => setItems([]));
  }, [doc.slug, doc.title_en]);
  if (!items.length) return null;
  const key = items[0].variety;
  const name = lang === "ar" ? items[0].variety_ar : items[0].variety_en;
  return (
    <section className="live-souq" data-testid="variety-live-souq">
      <div className="live-souq-head">
        <h2><Store size={18} /> {t("xl.liveSouq")}</h2>
        <p>{t("xl.liveSouqSub")}</p>
      </div>
      <div className="live-souq-grid">
        {items.slice(0, 6).map((l) => (
          <Link key={l.listing_id} to={`/souq/listing/${l.listing_id}`} className="live-souq-card glass-card" data-testid={`variety-souq-${l.listing_id}`}>
            <span>{l.farm_name} · {lang === "ar" ? l.region_ar : l.region_en}</span>
            <strong dir="ltr">{SAR(l.current_price, lang)}</strong>
            <small><Gavel size={11} /> {l.bid_count} {t("souq.bids")} · {l.quantity_kg} kg</small>
            <Countdown endsAt={l.ends_at} testId={`variety-souq-cd-${l.listing_id}`} />
          </Link>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <Link to={`/souq?variety=${encodeURIComponent(key)}`} className="gold-link" data-testid="variety-souq-all">{t("xl.allOfVariety").replace("{v}", name)} →</Link>
      </div>
    </section>
  );
}

export default function ContentDetailPage({ kind }) {
  const { slug } = useParams();
  const { lang, t } = useApp();
  const [doc, setDoc] = useState(null);
  useBackdrop(pickImage(doc?.hero_image, kind === "varieties" ? IMG.dates : kind === "routes" ? IMG.route : IMG.heritage));
  const [siblings, setSiblings] = useState([]);
  const [status, setStatus] = useState("loading");
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;
  const ArrowBack = lang === "ar" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    axios.get(`${API}/content/${kind}`).then((r) => setSiblings(r.data)).catch(() => {});
  }, [kind]);

  useEffect(() => {
    setStatus("loading");
    axios
      .get(`${API}/content/${kind}/${slug}`)
      .then((r) => { setDoc(r.data); setStatus("ok"); })
      .catch((e) => setStatus(e?.response?.status === 404 ? "notfound" : "error"));
  }, [kind, slug]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (status === "notfound") return <NotFoundPage />;
  if (status !== "ok") {
    return <main className="content-page"><div className="content-skeleton" data-testid="content-loading" /></main>;
  }

  const L = (k) => doc[`${k}_${lang}`];
  const focusHref = `/map?focus=${doc.map_focus}`;
  const rel = doc.related_items || {};
  const idx = siblings.findIndex((s) => s.slug === slug);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const nextItem = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;
  const sameRegion = doc.type === "variety" && doc.attrs?.region
    ? siblings.filter((s) => s.slug !== slug && s.attrs?.region === doc.attrs.region) : [];
  const originOasis = doc.type === "variety" ? { madinah: "al-madinah", qassim: "al-qassim", alahsa: "al-ahsa", alula: "alula", bisha: "bisha" }[doc.attrs?.region] : null;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": LD_TYPE[doc.type],
    name: L("title"),
    alternateName: doc[`title_${lang === "ar" ? "en" : "ar"}`],
    description: L("summary"),
    image: `${BASE}${doc.hero_image}`,
    url: `${BASE}/${kind}/${slug}`,
  };

  return (
    <main className="content-page" data-testid={`content-detail-${doc.type}`}>
      <Seo title={L("title")} description={L("summary")} image={doc.hero_image} type="article" jsonLd={jsonLd} />

      <header className="content-hero">
        <img src={doc.hero_image} alt={L("title")} className="content-hero-img" />
        <div className="content-hero-shade" />
        <div className="content-hero-inner">
          <Breadcrumbs items={[{ to: `/${kind}`, label: KIND_LABEL[kind][lang] }, { label: L("title") }]} />
          <span className="section-tag">{L("subtitle")}</span>
          <h1 className="content-title" data-testid="content-title">
            {L("title")}{" "}
            <span className="content-title-alt">{doc[`title_${lang === "ar" ? "en" : "ar"}`]}</span>
          </h1>
          <p className="content-summary">{L("summary")}</p>
          <Link to={focusHref} className="btn-lux-gold" data-testid="content-map-link">
            <MapPinned size={16} /> {t("content.viewOnMap")}
          </Link>
        </div>
      </header>

      <section className="content-facts" data-testid="content-facts">
        {(doc.facts || []).map((f) => (
          <div key={f.label_en} className="fact-cell" data-testid={`fact-${f.label_en.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="fact-label">{f[`label_${lang}`]}</span>
            <span className="fact-value">{f[`value_${lang}`]}</span>
          </div>
        ))}
      </section>

      <article className="content-body">
        {(doc.sections || []).map((s, i) => (
          <section key={i} className="content-section" data-testid={`content-section-${i}`}>
            <h2>{s[`heading_${lang}`]}</h2>
            <div className="rich" dangerouslySetInnerHTML={{ __html: s[`body_${lang}`] }} />
          </section>
        ))}
      </article>

      <nav className="cross-links" data-testid="cross-links">
        <Link to={focusHref} className="cross-link" data-testid="cross-link-map"><MapPinned size={14} /> {t("content.viewOnMap")}</Link>
        {originOasis && <Link to={`/oases/${originOasis}`} className="cross-link" data-testid="cross-link-oasis"><Landmark size={14} /> {t("detail.oasisLink")}</Link>}
        {doc.type === "variety" && doc.attrs?.region && <Link to={`/regions/${doc.attrs.region}`} className="cross-link" data-testid="cross-link-region"><MapPin size={14} /> {t("xl.growsIn")}: {t("xl.regionPage")}</Link>}
        <Link to={`/knowledge?q=${encodeURIComponent(lang === "ar" ? `حدثني عن ${L("title")}` : `Tell me about ${L("title")}`)}`} className="cross-link" data-testid="cross-link-knowledge">
          <Sparkles size={14} /> {t("detail.askKnowledge")} {L("title")}
        </Link>
      </nav>

      {doc.type === "variety" && <LiveSouq doc={doc} lang={lang} t={t} />}

      {sameRegion.length > 0 && (
        <section className="region-strip" data-testid="same-region-strip">
          <h3>{t("detail.sameRegion")}</h3>
          <div className="region-row">
            {sameRegion.map((s) => (
              <Link key={s.slug} to={`/varieties/${s.slug}`} className="glass-card" data-testid={`region-item-${s.slug}`}>
                <img src={s.hero_image} alt={s[`title_${lang}`]} loading="lazy" />
                <strong>{s[`title_${lang}`]}</strong>
              </Link>
            ))}
          </div>
        </section>
      )}

      {(prev || nextItem) && (
        <nav className="detail-nav" data-testid="detail-nav">
          {prev ? (
            <Link to={`/${kind}/${prev.slug}`} className="glass-card prev" data-testid="detail-prev">
              <ArrowBack size={20} />
              <span><small>{t("detail.prev")}</small><strong>{prev[`title_${lang}`]}</strong></span>
            </Link>
          ) : <span />}
          {nextItem ? (
            <Link to={`/${kind}/${nextItem.slug}`} className="glass-card next" data-testid="detail-next">
              <span><small>{t("detail.next")}</small><strong>{nextItem[`title_${lang}`]}</strong></span>
              <Arrow size={20} />
            </Link>
          ) : <span />}
        </nav>
      )}

      {Object.keys(rel).length > 0 && (
        <section className="related-wrap" data-testid="related-section">
          <h2 className="related-title">{t("content.related")}</h2>
          <div className="related-grid">
            {Object.entries(rel).map(([key, items]) =>
              items.map((it) =>
                key === "stories" ? (
                  <StoryCard key={it.slug} story={it} />
                ) : (
                  <ContentCard key={it.slug} item={it} kind={REL_KIND[key]} />
                )
              )
            )}
          </div>
          <Link to={`/${kind}`} className="back-link" data-testid="content-back-link">
            <Arrow size={14} /> {t("content.backTo")} {KIND_LABEL[kind][lang]}
          </Link>
        </section>
      )}
    </main>
  );
}
